import React, { useState } from 'react';
import { MoveUpRight } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { FormField } from '@epoch-ui/components/Form';
import { TimeZoneSelectorModal } from '@epoch-components/TimeZoneSelectorModal';
import { formatTimezoneDisplay } from 'utils/format';

/**
 * The display format for the time zone value.
 * - 'full': Shows full time zone name and offset.
 * - 'abbreviation': Shows only the abbreviation.
 */
type TimeZoneValueFormat = 'full' | 'abbreviation';
interface TimezoneSelectorFieldProps {
  /** The name of the form field (should match the form schema). */
  name: string;
  /** If true, disables the field and prevents interaction. */
  disabled?: boolean;
  /** If true, renders the field as read-only (no modal open). */
  readOnly?: boolean;
  /** Additional class names for the button. */
  className?: string;
  /** Display format for the time zone value. */
  valueFormat?: TimeZoneValueFormat;
}

/**
 * TimezoneSelectorField is a compound form field component for selecting a time zone.
 * It displays the current time zone in a button, and opens a modal for searching and selecting a new time zone.
 *
 * - Uses shadcn/ui Button and FormField for consistent styling.
 * - Integrates with react-hook-form for form state management.
 * - Uses a modal (TimeZoneSelectorModal) for time zone selection.
 *
 * @component
 * @example
 * <TimezoneSelectorField name="timezoneCode" />
 *
 * @param {TimezoneSelectorFieldProps} props - The props for the component.
 * @returns {React.ReactElement}
 */
const TimezoneSelectorField = ({
  name,
  disabled = false,
  readOnly = false,
  className,
  valueFormat = 'full'
}: TimezoneSelectorFieldProps): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <React.Fragment>
          <Button
            type="button"
            variant="outline"
            className={cn(
              'tw-flex tw-h-12 tw-shrink tw-grow tw-basis-0 tw-items-center tw-justify-between tw-rounded-md tw-px-3 tw-text-left tw-font-normal tw-text-black',
              className
            )}
            onClick={(): void => setOpen(true)}
            disabled={disabled || readOnly}
            title={field.value}
            onBlur={field.onBlur}
          >
            <span className="tw-truncate tw-text-sm">
              {valueFormat === 'abbreviation'
                ? `${formatTimezoneDisplay(field.value, 'short')}`
                : `(${formatTimezoneDisplay(field.value, 'shortOffset')}) ${formatTimezoneDisplay(
                    field.value,
                    'long'
                  )} ${formatTimezoneDisplay(field.value, 'short')}`}
            </span>
            {!readOnly && (
              <MoveUpRight size={20} className="tw-ml-2 tw-shrink-0 tw-text-primary-grey" />
            )}
          </Button>
          <TimeZoneSelectorModal
            isOpen={open}
            onRequestClose={() => setOpen(false)}
            onChange={field.onChange}
            timezoneCode={field.value}
          />
        </React.Fragment>
      )}
    />
  );
};

export { TimezoneSelectorField };
